import { Injectable, type Provider } from '@angular/core';

import { CommsBandService } from './comms-band.service';
import type { CommsBandPage } from './comms-band.models';
import { ok } from '../../../core/models/api-result';

/**
 * THE COMMS BAND, WITHOUT A SESSION.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS, AND IT IS NOT "FOR COMPLETENESS"
 * ─────────────────────────────────────────────────────────────────────────
 *
 * `broadcastFrontPage` is viewer-scoped, so unauthenticated it 401s and the
 * band renders NOWHERE. Every other read on this page has a preview and this
 * one did not, which had a specific and expensive consequence: the band was
 * the one surface on /hubs that could not be looked at before shipping.
 *
 * Two defects lived there because of it. Its cards could only be opened by
 * clicking the text — the picture and the strapline were dead to the pointer
 * — and pressing them opened nothing at all, because the read did not carry a
 * destination. Both were reported from a real signed-in session, twice, days
 * apart. Neither was findable in the harness because the harness had no band.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THE FIXTURE IS SHAPED TO SHOW
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Three cards, one per case that behaves differently:
 *
 *   1. a slot with its OWN label, pointing at content       — the ordinary card
 *   2. a slot with NO label, falling back to the post title — the case that
 *      silently dropped twelve of the twenty slots on dev
 *   3. a MANDATORY slot — the MUST READ badge
 *
 * Deliberately no LIVE slot: `openBandSlot` does not route one, and a fixture
 * that offers a press with no destination would put the original defect back
 * into the harness dressed as data.
 */
const PREVIEW_BAND: CommsBandPage = {
  collapsedToTicker: false,
  dressing: {
    layout: 'COVER',
    kicker: 'Chosen by your organisation',
    title: 'What autumn asks of us',
    sheet: '/assets/hubs-preview/pro-skyline.jpg',
    season: 'NONE',
    fromScope: 'ORGANISATION',
  },
  entries: [
    {
      kind: 'SLOT',
      slot: {
        id: 'slot-1',
        order: 1,
        priority: 'FEATURED',
        panelArt: '/assets/hubs-preview/pro-atrium.jpg',
        panelLabel: 'The office in October',
        strapline: 'Three minutes',
        liveEventId: null,
      liveEvent: null,
        external: null,
        target: 'CONTENT',
        // Points at a real row in HUBS_PREVIEW_ITEMS, so pressing it opens
        // something rather than deep-fetching an id nothing can resolve.
        itemId: 'bhm-1',
        item: { id: 'bhm-1', title: 'The office in October', kind: 'ARTICLE' },
        ackPolicy: null,
        viewerAcknowledgedAt: null,
      },
    },
    {
      kind: 'SLOT',
      slot: {
        id: 'slot-2',
        order: 2,
        priority: 'FEATURED',
        panelArt: '/assets/hubs-preview/pro-lab.jpg',
        // NO LABEL. The card takes the post's title instead of being dropped.
        panelLabel: null,
        strapline: 'From the lab',
        liveEventId: null,
      liveEvent: null,
        external: null,
        target: 'CONTENT',
        itemId: 'lab-1',
        item: { id: 'lab-1', title: 'What the lab learned this quarter', kind: 'ARTICLE' },
        ackPolicy: null,
        viewerAcknowledgedAt: null,
      },
    },
    {
      kind: 'SLOT',
      slot: {
        id: 'slot-3',
        order: 3,
        priority: 'MANDATORY',
        panelArt: '/assets/hubs-preview/pro-reception.jpg',
        panelLabel: 'Security refresh — everyone',
        strapline: 'Must read',
        liveEventId: null,
      liveEvent: null,
        external: null,
        target: 'CONTENT',
        itemId: 'rdx-1',
        item: { id: 'rdx-1', title: 'Security refresh', kind: 'ARTICLE' },
        // The MANDATORY slot, and it is OUTSTANDING — the harness has to be
        // able to show the badge and the Acknowledge control at all. A
        // fixture where everything is already settled can only ever exercise
        // the half that is easy to get right.
        ackPolicy: { requiredBy: '2026-09-30T17:00:00Z', remindEveryHours: 24 },
        viewerAcknowledgedAt: null,
      },
    },
  ],
};

@Injectable()
export class CommsBandPreviewService extends CommsBandService {
  protected override async fetch() {
    return ok(PREVIEW_BAND);
  }
}

export const COMMS_BAND_PREVIEW_PROVIDER: Provider = {
  provide: CommsBandService,
  useClass: CommsBandPreviewService,
};
